/**
 * Sprint 76 — Rapprochement bancaire SaaS (Averdy Agency v2).
 * Dashboard + Transactions (10 statuts + filtres) + Anomalies + Import PDF.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Banknote, AlertCircle, CheckCircle2, Wallet, Upload, Search,
  RefreshCw, TrendingUp, TrendingDown, Loader2, Sparkles, FileText,
  ChevronDown, ChevronRight, Link2, Trash2, Download, FileSpreadsheet,
  ArrowUpRight, ArrowDownRight, Link as LinkIcon, MoreHorizontal,
  Maximize2, Minimize2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  non_rapproche: { label: "Non justifiée", color: "bg-orange-100 text-orange-800" },
  en_attente_validation: { label: "À valider", color: "bg-yellow-100 text-yellow-800" },
  rapproche_auto: { label: "Auto ✓", color: "bg-emerald-100 text-emerald-800" },
  rapproche_manuel: { label: "Manuel ✓", color: "bg-emerald-100 text-emerald-800" },
  rapproche_groupe: { label: "Groupé ✓", color: "bg-indigo-100 text-indigo-800" },
  rapproche_remise_sepa: { label: "Remise SEPA", color: "bg-purple-100 text-purple-800" },
  divers: { label: "Traité (divers)", color: "bg-slate-100 text-slate-700" },
  ignore: { label: "Ignoré", color: "bg-red-100 text-red-700" },
  sans_correspondance: { label: "Sans corresp.", color: "bg-amber-100 text-amber-800" },
  rejet_sepa: { label: "Rejet SEPA", color: "bg-rose-100 text-rose-800" },
};

const eur = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR" }).format(v || 0);
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return d; } };

export default function Reconciliation() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "dashboard";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    // Sync tab query param when user changes tab manually
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTab(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const handleTabChange = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    // Reset tx focus when switching tabs
    if (v !== "transactions") next.delete("tx");
    setSearchParams(next, { replace: true });
  };
  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Banknote className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Rapprochement bancaire</h1>
            <p className="text-sm text-slate-500">
              10 statuts · Import PDF via IA · Règles mémorisées · Détection rejets SEPA
            </p>
          </div>
        </div>
        <ExportButton />
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-recon-dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-recon-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="remises" data-testid="tab-recon-remises">Remises SEPA</TabsTrigger>
          <TabsTrigger value="anomalies" data-testid="tab-recon-anomalies">Anomalies</TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-recon-statements">Relevés bancaires</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-4"><Dashboard /></TabsContent>
        <TabsContent value="transactions" className="mt-4"><Transactions /></TabsContent>
        <TabsContent value="remises" className="mt-4"><RemisesSepa /></TabsContent>
        <TabsContent value="anomalies" className="mt-4"><Anomalies /></TabsContent>
        <TabsContent value="statements" className="mt-4"><Statements /></TabsContent>
      </Tabs>
    </div>
  );
}

// =========================================================================
// EXPORT EXCEL
// =========================================================================
function ExportButton() {
  const [open, setOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/reconciliation/export/preview?${params}`)
       .then((r) => setPreview(r.data))
       .catch(() => setPreview(null));
  }, [open, dateFrom, dateTo]);

  const download = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const resp = await fetch(`${API}/reconciliation/export/xlsx?${params}`,
                                 { credentials: "include" });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clarix_rapprochement_${dateFrom || "all"}_${dateTo || "all"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
      setOpen(false);
    } catch (e) { toast.error(e.message?.slice(0, 200) || "Export échoué"); }
    finally { setDownloading(false); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline"
               data-testid="open-export-dialog">
        <FileSpreadsheet className="w-4 h-4 mr-2" />Exporter Excel
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              Export Excel rapprochement
            </DialogTitle>
            <DialogDescription>
              5 feuilles : Résumé, Transactions, Rapprochées, Anomalies, Remises SEPA.
              Format compatible expert-comptable.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Du</label>
                <Input type="date" value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        data-testid="export-date-from" />
              </div>
              <div>
                <label className="text-sm font-medium">Au</label>
                <Input type="date" value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        data-testid="export-date-to" />
              </div>
            </div>
            {preview && (
              <div className="bg-slate-50 p-3 rounded text-sm">
                <p><span className="font-semibold">{preview.transactions_count}</span> transactions à exporter</p>
                <p><span className="font-semibold">{preview.remises_count}</span> remises SEPA</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={download} disabled={downloading}
                     data-testid="download-xlsx-btn">
              {downloading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</>
                : <><Download className="w-4 h-4 mr-2" />Télécharger .xlsx</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// =========================================================================
// DASHBOARD
// =========================================================================
function Dashboard() {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [lastMatchResult, setLastMatchResult] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [prefsSaving, setPrefsSaving] = useState(false);

  const load = () => api.get("/reconciliation/dashboard")
    .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  const loadPrefs = () => api.get("/banking/preferences")
    .then((r) => setPrefs(r.data)).catch(() => setPrefs({ auto_match_enabled: true }));
  useEffect(() => { load(); loadPrefs(); }, []);

  const togglePrefs = async () => {
    if (!prefs) return;
    setPrefsSaving(true);
    try {
      const next = !prefs.auto_match_enabled;
      const r = await api.put("/banking/preferences", { auto_match_enabled: next });
      setPrefs((p) => ({ ...(p || {}), auto_match_enabled: r.data.auto_match_enabled }));
      toast.success(next ? "Auto-rapprochement v4 activé sur les syncs Bridge" : "Auto-rapprochement désactivé");
    } catch (e) { toast.error(apiError(e)); }
    finally { setPrefsSaving(false); }
  };

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/reconciliation/scan-rejections");
      toast.success(`${r.data.tagged_as_rejection} rejet(s) SEPA détecté(s) sur ${r.data.scanned} transactions`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setScanning(false); }
  };

  const autoMatchV4 = async () => {
    setAutoMatching(true);
    setLastMatchResult(null);
    try {
      const r = await api.post("/reconciliation/auto-match-scan");
      setLastMatchResult(r.data);
      const total = (r.data.auto_linked || 0) + (r.data.learned_applied || 0);
      toast.success(
        `Scan v4 : ${total} rapprochement(s) auto (${r.data.auto_linked} règles + ${r.data.learned_applied} apprises) sur ${r.data.scanned} transactions`
      );
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAutoMatching(false); }
  };

  if (!data) return <div className="text-slate-500 text-sm">Chargement…</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi icon={Wallet} label="Solde bancaire" value={eur(data.solde_bancaire)}
             hint={`${data.accounts_count} compte(s)`} color="text-blue-600" />
        <Kpi icon={TrendingUp} label="Transactions" value={data.total_transactions}
             color="text-slate-800" />
        <Kpi icon={AlertCircle} label="Non justifiées" value={data.non_justifiees}
             color="text-orange-600" />
        <Kpi icon={AlertCircle} label="Rejets SEPA" value={data.rejets_sepa}
             color="text-rose-600" />
      </div>

      <Card>
        <CardHeader className="flex flex-row justify-between items-start">
          <div>
            <CardTitle className="text-base">Répartition par statut</CardTitle>
            <p className="text-sm text-slate-500">
              10 statuts alignés flow bancaire · {prefs?.auto_match_enabled === false
                ? "Auto-rapprochement post-sync : désactivé"
                : "Auto-rapprochement v4 actif à chaque sync Bridge"}
            </p>
          </div>
          <div className="flex gap-2">
            {prefs && (
              <Button variant="ghost" size="sm" onClick={togglePrefs}
                       disabled={prefsSaving} data-testid="toggle-auto-match-pref"
                       className={prefs.auto_match_enabled
                         ? "text-emerald-700 hover:bg-emerald-50"
                         : "text-slate-500 hover:bg-slate-50"}>
                {prefsSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  : <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                      prefs.auto_match_enabled ? "bg-emerald-500" : "bg-slate-400"
                    }`} />}
                Auto {prefs.auto_match_enabled ? "ON" : "OFF"}
              </Button>
            )}
            <Button variant="default" size="sm" onClick={autoMatchV4} disabled={autoMatching}
                     data-testid="auto-match-v4-btn"
                     className="bg-blue-600 hover:bg-blue-700">
              {autoMatching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1" />}
              Auto-rapprochement v4
            </Button>
            <Button variant="outline" size="sm" onClick={scan} disabled={scanning}
                     data-testid="scan-rejections-btn">
              {scanning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Scanner rejets SEPA
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {lastMatchResult && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded text-sm"
                  data-testid="auto-match-result">
              <p className="font-semibold text-blue-900 mb-1">Résultat du scan v4</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                <div><span className="text-slate-500">Scannées :</span> <strong>{lastMatchResult.scanned}</strong></div>
                <div><span className="text-slate-500">Auto (P1) :</span> <strong className="text-emerald-700">{lastMatchResult.auto_linked}</strong></div>
                <div><span className="text-slate-500">Règles apprises :</span> <strong className="text-blue-700">{lastMatchResult.learned_applied}</strong></div>
                <div><span className="text-slate-500">Transferts internes :</span> <strong>{lastMatchResult.internal_transfers}</strong></div>
                <div><span className="text-slate-500">Sans match :</span> <strong className="text-orange-700">{lastMatchResult.no_match}</strong></div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {Object.entries(STATUS_META).map(([k, m]) => {
              const s = data.by_status[k] || { count: 0, total: 0 };
              return (
                <div key={k} className="border rounded p-2" data-testid={`stat-${k}`}>
                  <Badge className={m.color}>{m.label}</Badge>
                  <p className="text-lg font-semibold mt-1">{s.count}</p>
                  <p className="text-xs text-slate-500">{eur(s.total)}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {data.bridge_status.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Connexions bancaires</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {data.bridge_status.map((b) => (
              <div key={b.id} className="flex items-center justify-between border rounded px-3 py-2">
                <span className="text-sm">{b.bank_name || `Élément n°${b.id}`}</span>
                {b.needs_action
                  ? <Badge className="bg-red-100 text-red-700">⚠ Action requise (code {b.status})</Badge>
                  : <Badge className="bg-emerald-100 text-emerald-700">Connectée</Badge>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, color }) {
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <p className="text-2xl font-semibold mt-2">{value}</p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </CardContent></Card>
  );
}

// =========================================================================
// TRANSACTIONS
// =========================================================================
function Transactions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusTxId = searchParams.get("tx");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedTx, setSelectedTx] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // Charge les comptes bancaires une fois pour afficher le nom du compte
  // sur chaque ligne (parité avec Trésorerie).
  useEffect(() => {
    api.get("/banking/accounts")
       .then((r) => setAccounts(r.data.accounts || []))
       .catch(() => {});
  }, []);
  const accountsById = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.id, a])),
    [accounts]);

  const load = () => {
    setLoading(true);
    const params = { page, page_size: 25 };
    if (status !== "all") params.status = status;
    if (search) params.search = search;
    api.get("/reconciliation/transactions", { params })
       .then((r) => { setRows(r.data.data); setTotal(r.data.total); })
       .catch((e) => toast.error(apiError(e)))
       .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status, page]);
  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Deep-link: quand ?tx=<id>, fetch la tx directement et ouvre la modale
  useEffect(() => {
    if (!focusTxId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/reconciliation/transactions/${focusTxId}`);
        if (!cancelled && r.data) setSelectedTx(r.data);
      } catch (e) {
        if (!cancelled) toast.error("Transaction introuvable");
      }
    })();
    return () => { cancelled = true; };
  }, [focusTxId]);

  const closeSelected = () => {
    setSelectedTx(null);
    if (focusTxId) {
      const next = new URLSearchParams(searchParams);
      next.delete("tx");
      setSearchParams(next, { replace: true });
    }
    load();
  };

  const unreconcile = async (tx, e) => {
    e.stopPropagation();
    try {
      await api.post(`/banking/transactions/${tx.id}/unreconcile`);
      toast.success("Rapprochement annulé"); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
          <Input className="pl-8" placeholder="Rechercher description, montant, contrepartie…"
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 data-testid="tx-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-64" data-testid="tx-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-slate-500 text-sm">Chargement…</div>
          : rows.length === 0 ? (
              <div className="py-16 text-center">
                <Banknote className="h-10 w-10 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm text-slate-500">Aucune transaction pour ces filtres.</p>
              </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left p-3 w-28">Date</th>
                  <th className="text-left p-3">Description</th>
                  <th className="text-right p-3 w-32">Montant</th>
                  <th className="text-left p-3 w-56">Rapprochement</th>
                  <th className="text-right p-3 w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const negative = (r.amount || 0) < 0;
                  const acct = accountsById[r.account_id];
                  const linked = r.reconciled_with_id;
                  const desc = r.clean_description || r.provider_description || r.description || "—";
                  const meta = STATUS_META[r.reconciliation_status] || STATUS_META.non_rapproche;
                  const isRemSdd = r.is_rem_sdd || !!r.remise_sepa_id;
                  const remiseMatched = r.remise_matched_count;
                  const remiseTotal = r.remise_total_count;
                  return (
                    <tr key={r.id}
                        className="border-b hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedTx(r)}
                        data-testid={`tx-row-${r.id}`}>
                      <td className="p-3 text-xs tabular-nums text-slate-600 whitespace-nowrap">
                        {fmtDate(r.date)}
                      </td>
                      <td className="p-3 max-w-lg">
                        <div className="text-sm text-slate-900 truncate" title={desc}>
                          {desc}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {acct && <span className="truncate">{acct.name}</span>}
                          {r.category && (
                            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">
                              {r.category}
                            </span>
                          )}
                          {isRemSdd && r.remise_sepa_id && remiseTotal > 0 && (
                            <span data-testid={`tx-rem-sdd-progress-${r.id}`}
                                  className={cn(
                                    "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
                                    remiseMatched >= remiseTotal
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-amber-100 text-amber-800"
                                  )}>
                              REM SDD · {remiseMatched}/{remiseTotal} rapprochées
                            </span>
                          )}
                          {isRemSdd && !r.remise_sepa_id && (
                            <span data-testid={`tx-rem-sdd-no-import-${r.id}`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800"
                                  title="Cette ligne agrège plusieurs prélèvements clients — importez le PDF de la remise pour la décomposer.">
                              REM SDD · importer PDF
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={cn("p-3 text-right font-mono font-medium tabular-nums whitespace-nowrap",
                                        negative ? "text-slate-900" : "text-emerald-700")}>
                        <span className="inline-flex items-center gap-1">
                          {negative
                            ? <ArrowDownRight className="h-3.5 w-3.5" strokeWidth={2} />
                            : <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />}
                          {eur(r.amount)}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <Badge className={cn("text-[10px] w-fit", meta.color)}>
                            {meta.label}
                          </Badge>
                          {linked && (
                            <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                              <LinkIcon className="h-3 w-3" strokeWidth={2} />
                              {r.reconciled_with_kind === "customer"
                                ? "Facture client"
                                : r.reconciled_with_kind === "supplier"
                                  ? "Facture fournisseur"
                                  : "Lien"}
                            </span>
                          )}
                          {r.reconciliation_reason && !linked && (
                            <span className="text-[10px] text-slate-400 truncate max-w-[180px]"
                                  title={r.reconciliation_reason}>
                              {r.reconciliation_reason}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        {linked ? (
                          <div className="inline-flex gap-1">
                            <Button variant="ghost" size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const url = r.reconciled_with_kind === "supplier"
                                        ? `/app/purchases/supplier-invoices/${r.reconciled_with_id}`
                                        : `/app/sales/invoices/${r.reconciled_with_id}`;
                                      window.open(url, "_blank", "noopener");
                                    }}
                                    data-testid={`tx-view-invoice-${r.id}`}
                                    className="h-7 text-xs text-blue-600 hover:text-blue-800">
                              <FileText className="h-3 w-3 mr-1" />
                              Voir facture
                            </Button>
                            <Button variant="ghost" size="sm"
                                    onClick={(e) => unreconcile(r, e)}
                                    data-testid={`tx-unreconcile-${r.id}`}
                                    className="h-7 text-xs text-slate-600 hover:text-red-600">
                              <Trash2 className="h-3 w-3 mr-1" />
                              Défaire
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="outline" size="sm"
                                    onClick={(e) => { e.stopPropagation(); setSelectedTx(r); }}
                                    data-testid={`tx-reconcile-${r.id}`}
                                    className="h-7 text-xs">
                              <LinkIcon className="h-3 w-3 mr-1" strokeWidth={2} />
                              Rapprocher
                            </Button>
                            <Button variant="outline" size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedTx({ ...r, __openTab: "group" });
                                    }}
                                    data-testid={`tx-group-${r.id}`}
                                    title="Rapprocher plusieurs factures avec cette transaction (paiement groupé)"
                                    className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50">
                                <span className="text-[10px] mr-1 font-bold">N</span>
                                Grouper
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between items-center text-sm">
        <span className="text-slate-500">{total} transaction(s)</span>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}>Précédent</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(page + 1)}
                  disabled={rows.length < 25}>Suivant</Button>
        </div>
      </div>

      {selectedTx && <TxDialog tx={selectedTx} onClose={closeSelected} />}
    </div>
  );
}

function TxDialog({ tx, onClose }) {
  const [mode, setMode] = useState(tx.__openTab || "suggestions"); // suggestions | status | group | alloc

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={mode === "suggestions" || mode === "status"
                                 ? "max-w-3xl" : "max-w-4xl"}>
        <DialogHeader>
          <DialogTitle className="pr-8">{tx.description}</DialogTitle>
          <DialogDescription>
            {fmtDate(tx.date)} ·{" "}
            <span className={tx.amount < 0 ? "text-rose-600" : "text-emerald-600"}>
              {eur(tx.amount)}
            </span>
            {" · "}
            <span className="text-slate-500">
              {tx.amount < 0 ? "Débit → facture fournisseur" : "Crédit → facture client"}
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Onglets internes toujours visibles */}
        <div className="flex flex-wrap gap-1 border-b border-slate-200 -mx-6 px-6 pb-0">
          {[
            { key: "suggestions", label: "1 facture", testid: "tx-tab-suggestions" },
            { key: "group",       label: "Plusieurs factures (client paie plusieurs en 1 fois)", testid: "tx-tab-group", highlight: true },
            { key: "alloc",       label: "Paiement partiel", testid: "tx-tab-alloc" },
            { key: "status",      label: "Statut / note", testid: "tx-tab-status" },
          ].map((t) => (
            <button key={t.key} onClick={() => setMode(t.key)}
                    data-testid={t.testid}
                    className={cn(
                      "px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors",
                      mode === t.key
                        ? "border-blue-600 text-blue-700"
                        : t.highlight
                          ? "border-transparent text-purple-700 hover:text-purple-900 hover:bg-purple-50"
                          : "border-transparent text-slate-500 hover:text-slate-900")}>
              {t.label}
            </button>
          ))}
        </div>

        {mode === "suggestions" ? (
          <SuggestionsPanel tx={tx} onDone={onClose} />
        ) : mode === "status" ? (
          <StatusPanel tx={tx} onDone={onClose} />
        ) : mode === "group" ? (
          <GroupMatchPanel tx={tx} onClose={onClose} onBack={() => setMode("suggestions")} />
        ) : (
          <AllocationsPanel tx={tx} onClose={onClose} onBack={() => setMode("suggestions")} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// SuggestionsPanel — vue par défaut : liste des factures candidates avec
// bouton "Rapprocher cette facture" en 1 clic. Inspiré de Qonto / Pennylane.
// ---------------------------------------------------------------------------
function SuggestionsPanel({ tx, onDone }) {
  const [candidates, setCandidates] = useState([]);
  const [txKind, setTxKind] = useState(tx.amount < 0 ? "supplier" : "customer");
  const [loading, setLoading] = useState(true);
  const [linkingId, setLinkingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/banking/transactions/${tx.id}/candidates`)
       .then((r) => {
         setCandidates(r.data.candidates || []);
         if (r.data.kind) setTxKind(r.data.kind);
       })
       .catch((e) => toast.error(apiError(e)))
       .finally(() => setLoading(false));
  }, [tx.id]);

  const linkOne = async (c) => {
    setLinkingId(c.invoice_id);
    try {
      await api.post(`/banking/transactions/${tx.id}/reconcile`, {
        invoice_kind: c.kind,
        invoice_id: c.invoice_id,
      });
      toast.success(`✓ Rapproché avec facture ${c.invoice_number || "?"}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLinkingId(null); }
  };

  const scoreBadge = (score) => {
    if (score >= 90) return { cls: "bg-emerald-100 text-emerald-700", label: "Excellent" };
    if (score >= 70) return { cls: "bg-blue-100 text-blue-700",       label: "Bon" };
    if (score >= 50) return { cls: "bg-amber-100 text-amber-700",     label: "Moyen" };
    return              { cls: "bg-slate-100 text-slate-600",       label: "Faible" };
  };

  return (
    <div className="space-y-3" data-testid="suggestions-panel">
      {loading ? (
        <div className="py-8 text-center text-slate-500 text-sm">
          Recherche des factures compatibles…
        </div>
      ) : candidates.length === 0 ? (
        <div className="py-10 text-center">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-slate-700 font-medium">Aucune facture compatible</p>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Aucune facture {txKind === "supplier" ? "fournisseur" : "client"} non payée n'a
            été trouvée avec un montant proche de {eur(tx.amount)} dans les ±30 jours.
            Essaie <b>Paiement partiel</b> (pour rapprocher partiellement une facture plus
            grosse) ou <b>Grouper N factures</b> si cette tx couvre plusieurs factures.
          </p>
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-500">
            {candidates.length} facture{candidates.length > 1 ? "s" : ""} triée{candidates.length > 1 ? "s" : ""} par pertinence
            (montant + proximité de date + nom du tiers)
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="p-2 text-left">N° facture</th>
                  <th className="p-2 text-left">Tiers</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-right">TTC</th>
                  <th className="p-2 text-right">Reste dû</th>
                  <th className="p-2 text-center">Pertinence</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => {
                  const badge = scoreBadge(c.score);
                  return (
                    <tr key={c.invoice_id}
                        className={cn("border-t border-slate-100 hover:bg-slate-50",
                                      c.too_old && "bg-amber-50/40")}
                        data-testid={`suggestion-row-${c.invoice_id}`}>
                      <td className="p-2 font-mono text-blue-700">{c.invoice_number || "—"}</td>
                      <td className="p-2 max-w-[180px]">
                        <div className="truncate" title={c.party_name}>{c.party_name || "—"}</div>
                      </td>
                      <td className="p-2 text-xs text-slate-600 whitespace-nowrap">
                        {fmtDate(c.invoice_date)}
                        {c.days_delta !== null && c.days_delta !== undefined && (
                          <div className={cn("text-[10px]",
                                             c.too_old ? "text-amber-700 font-semibold" : "text-slate-400")}>
                            {c.too_old
                              ? `⚠ ${Math.round(c.days_delta / 30)} mois d'écart`
                              : c.days_delta === 0 ? "même jour" : `${c.days_delta} j d'écart`}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono">{eur(c.total_ttc)}</td>
                      <td className="p-2 text-right font-mono font-medium">{eur(c.remaining_ttc)}</td>
                      <td className="p-2 text-center">
                        <span className={cn("text-[10px] px-2 py-0.5 rounded font-medium", badge.cls)}>
                          {badge.label} · {c.score}
                        </span>
                      </td>
                      <td className="p-2 text-right">
                        {c.file_asset_id && (
                          <Button variant="ghost" size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`${API}/files/${c.file_asset_id}`,
                                                "_blank", "noopener");
                                  }}
                                  title="Ouvrir la facture (PDF)"
                                  data-testid={`suggestion-view-${c.invoice_id}`}
                                  className="h-7 w-7 mr-1">
                            <FileText className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="sm"
                                onClick={() => linkOne(c)}
                                disabled={linkingId !== null}
                                data-testid={`suggestion-link-${c.invoice_id}`}
                                className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white">
                          {linkingId === c.invoice_id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <><LinkIcon className="h-3 w-3 mr-1" />Rapprocher</>}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusPanel — panneau existant "statut + note" (déplacé de l'ancien mode
// single vers son propre onglet, pour ne plus être le mode par défaut).
// ---------------------------------------------------------------------------
function StatusPanel({ tx, onDone }) {
  const [status, setStatus] = useState(tx.reconciliation_status || "non_rapproche");
  const [note, setNote] = useState(tx.note || "");
  const save = async () => {
    try {
      await api.post(`/reconciliation/transactions/${tx.id}/set-status`,
                      { status, note });
      toast.success("Statut mis à jour");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="status-panel">
      <div>
        <label className="text-sm font-medium">Statut</label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="tx-set-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium">Note</label>
        <Input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Contexte, référence…" data-testid="tx-note" />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>Annuler</Button>
        <Button onClick={save} data-testid="tx-save-btn">Enregistrer</Button>
      </DialogFooter>
    </div>
  );
}

function GroupMatchPanel({ tx, onClose, onBack }) {
  const [candidates, setCandidates] = useState([]);
  const [kind, setKind] = useState("customer");
  const [selected, setSelected] = useState({});
  const [txAmount, setTxAmount] = useState(Math.abs(tx.amount || 0));
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/reconciliation/transactions/${tx.id}/group-candidates`)
       .then((r) => {
         setCandidates(r.data.candidates);
         setKind(r.data.kind);
         setTxAmount(r.data.tx_amount);
       })
       .catch((e) => toast.error(apiError(e)))
       .finally(() => setLoading(false));
  }, [tx.id]);

  const askSuggestions = async () => {
    setSuggesting(true);
    try {
      const r = await api.get(`/reconciliation/transactions/${tx.id}/suggest-group`);
      setSuggestions(r.data.suggestions || []);
      if ((r.data.suggestions || []).length === 0) {
        toast.info("Aucune combinaison exacte trouvée");
      } else {
        toast.success(`${r.data.suggestions.length} combinaison(s) proposée(s) par l'IA`);
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSuggesting(false); }
  };

  const applySuggestion = (sug) => {
    const next = {};
    for (const iid of sug.invoice_ids) next[iid] = true;
    setSelected(next);
    toast.success(`${sug.count} facture(s) présélectionnée(s), écart ${eur(sug.delta)}`);
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectedTotal = candidates
    .filter((c) => selected[c.invoice_id])
    .reduce((s, c) => s + (c.total_ttc || 0), 0);
  const delta = txAmount - selectedTotal;
  const canSubmit = selectedIds.length > 0 && Math.abs(delta) <= 0.05;

  const submit = async () => {
    try {
      const r = await api.post(`/reconciliation/transactions/${tx.id}/match-group`,
                                 { invoice_kind: kind, invoice_ids: selectedIds });
      toast.success(`✓ ${r.data.matched_count} facture(s) rapprochée(s) — écart ${eur(r.data.delta)}`);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="bg-slate-50 p-3 rounded flex items-center justify-between text-sm">
        <div>
          Montant transaction : <span className="font-mono font-semibold">{eur(txAmount)}</span> ·
          Type : <Badge variant="outline">{kind === "supplier" ? "Achat" : "Vente"}</Badge>
        </div>
        <div>
          Sélectionné : <span className="font-mono font-semibold">{eur(selectedTotal)}</span> ·
          Écart : <span className={`font-mono font-semibold ${Math.abs(delta) <= 0.05 ? "text-emerald-600" : "text-rose-600"}`}>
            {delta >= 0 ? "" : "−"}{eur(Math.abs(delta))}
          </span>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <Button size="sm" variant="outline" onClick={askSuggestions}
                 disabled={suggesting}
                 data-testid="suggest-btn"
                 className="border-purple-200 text-purple-700 hover:bg-purple-50">
          {suggesting
            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Calcul…</>
            : <><Sparkles className="w-3 h-3 mr-1" />Suggérer une combinaison</>}
        </Button>
        <span className="text-xs text-slate-500">{candidates.length} candidate(s)</span>
      </div>

      {suggestions.length > 0 && (
        <div className="border rounded p-2 bg-purple-50 space-y-1">
          {suggestions.slice(0, 3).map((s, i) => (
            <button key={i} onClick={() => applySuggestion(s)}
                     className="w-full text-left text-xs p-2 rounded bg-white hover:bg-purple-100 flex items-center justify-between"
                     data-testid={`suggestion-${i}`}>
              <span>
                <b>{s.count} facture(s)</b> · Total {eur(s.total)} · Écart {eur(s.delta)}
              </span>
              <span className="text-purple-600">→ Appliquer</span>
            </button>
          ))}
        </div>
      )}

      {loading ? <div className="p-6 text-center text-slate-500">Recherche des candidats…</div>
      : candidates.length === 0 ? <div className="p-6 text-center text-slate-500">Aucune facture ouverte compatible.</div>
      : <div className="max-h-72 overflow-y-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-500 sticky top-0">
              <tr>
                <th className="p-2"></th>
                <th className="text-left p-2">N°</th>
                <th className="text-left p-2">Client / Fournisseur</th>
                <th className="text-left p-2">Date</th>
                <th className="text-right p-2">Montant TTC</th>
                <th className="text-right p-2">Écart</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr key={c.invoice_id} className={`border-t hover:bg-slate-50 ${selected[c.invoice_id] ? "bg-blue-50" : ""}`}
                     data-testid={`candidate-${c.invoice_id}`}>
                  <td className="p-2">
                    <input id={`recon-select-invoice-${c.invoice_id}`} name={`recon-select-invoice-${c.invoice_id}`}
                           type="checkbox" checked={!!selected[c.invoice_id]}
                           onChange={(e) => setSelected({ ...selected, [c.invoice_id]: e.target.checked })} />
                  </td>
                  <td className="p-2 font-mono text-xs">{c.invoice_number}</td>
                  <td className="p-2">{c.party_name}</td>
                  <td className="p-2 text-xs">{fmtDate(c.invoice_date)}</td>
                  <td className="p-2 text-right font-mono">{eur(c.total_ttc)}</td>
                  <td className="p-2 text-right font-mono text-xs text-slate-500">{eur(c.delta_from_tx)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      <DialogFooter>
        <Button variant="outline" onClick={onBack}>← Retour</Button>
        <Button onClick={submit} disabled={!canSubmit}
                data-testid="submit-group-match-btn">
          <Link2 className="w-4 h-4 mr-2" />
          Grouper {selectedIds.length} facture(s)
        </Button>
      </DialogFooter>
    </div>
  );
}

// =========================================================================
// ALLOCATIONS — paiement partiel / split personnalisé (Sprint P1a)
// =========================================================================
function AllocationsPanel({ tx, onClose, onBack }) {
  const [candidates, setCandidates] = useState([]);
  const [kind, setKind] = useState("customer");
  const [txAmount, setTxAmount] = useState(Math.abs(tx.amount || 0));
  const [allocations, setAllocations] = useState({}); // {invoice_id: amount}
  const [partyFilter, setPartyFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = (party) => {
    setLoading(true);
    const params = party ? { params: { party } } : {};
    api.get(`/reconciliation/transactions/${tx.id}/partial-candidates`, params)
       .then((r) => {
         setCandidates(r.data.candidates || []);
         setKind(r.data.kind);
         setTxAmount(r.data.tx_amount);
       })
       .catch((e) => toast.error(apiError(e)))
       .finally(() => setLoading(false));
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, [tx.id]);

  const applyPartyFilter = () => load(partyFilter.trim());

  // Auto-remplit chaque facture cochée avec min(remaining, montant restant à allouer)
  const autoFill = (invId, remaining) => {
    setAllocations((prev) => {
      const next = { ...prev };
      if (next[invId] !== undefined) {
        delete next[invId];
        return next;
      }
      const already = Object.values(next).reduce((s, v) => s + (Number(v) || 0), 0);
      const left = Math.max(0, txAmount - already);
      next[invId] = Math.min(remaining, left).toFixed(2);
      return next;
    });
  };

  const setAmount = (invId, raw) => {
    setAllocations((prev) => ({ ...prev, [invId]: raw }));
  };

  const totalAllocated = Object.values(allocations)
    .reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const remainingTx = txAmount - totalAllocated;
  const selectedIds = Object.keys(allocations).filter(
    (k) => parseFloat(allocations[k]) > 0);
  const canSubmit = selectedIds.length > 0
    && totalAllocated > 0.001
    && totalAllocated <= txAmount + 0.05;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        invoice_kind: kind,
        allocations: selectedIds.map((id) => ({
          invoice_id: id,
          amount: parseFloat(allocations[id]),
        })),
      };
      const r = await api.post(
        `/reconciliation/transactions/${tx.id}/match-allocations`, payload);
      const settled = (r.data.allocations || []).filter((a) => a.fully_settled).length;
      const partial = r.data.matched_count - settled;
      toast.success(
        `✓ ${r.data.matched_count} facture(s) rapprochée(s) — ${settled} soldée(s), ${partial} partielle(s) · Écart ${eur(r.data.delta)}`);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || apiError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3" data-testid="allocations-panel">
      <div className="bg-slate-50 p-3 rounded space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <div>
            Transaction : <span className="font-mono font-semibold">{eur(txAmount)}</span> ·
            Type : <Badge variant="outline">{kind === "supplier" ? "Achat" : "Vente"}</Badge>
          </div>
          <div>
            Alloué : <span className="font-mono font-semibold text-blue-700">{eur(totalAllocated)}</span> ·
            Reste : <span className={`font-mono font-semibold ${
              Math.abs(remainingTx) <= 0.05 ? "text-emerald-600"
              : remainingTx < 0 ? "text-rose-600" : "text-amber-600"
            }`}>
              {eur(remainingTx)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 whitespace-nowrap">Filtrer par tiers :</label>
          <Input value={partyFilter} onChange={(e) => setPartyFilter(e.target.value)}
                  placeholder="ex : Cabinet Meunier" className="h-8 text-xs"
                  data-testid="alloc-party-input"
                  onKeyDown={(e) => e.key === "Enter" && applyPartyFilter()} />
          <Button size="sm" variant="outline" onClick={applyPartyFilter}
                  data-testid="alloc-party-apply">
            <Search className="w-3 h-3" />
          </Button>
          {partyFilter && (
            <Button size="sm" variant="ghost" onClick={() => { setPartyFilter(""); load(""); }}>
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-slate-500">Recherche des factures ouvertes…</div>
      ) : candidates.length === 0 ? (
        <div className="p-6 text-center text-slate-500">
          Aucune facture ouverte compatible.
          {partyFilter && " Essayez d'élargir le filtre de tiers."}
        </div>
      ) : (
        <div className="max-h-[380px] overflow-y-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-500 sticky top-0">
              <tr>
                <th className="p-2 w-10"></th>
                <th className="text-left p-2">N°</th>
                <th className="text-left p-2">Tiers</th>
                <th className="text-left p-2">Échéance</th>
                <th className="text-right p-2">TTC</th>
                <th className="text-right p-2">Reste dû</th>
                <th className="text-right p-2 w-36">Alloué (€)</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => {
                const selected = allocations[c.invoice_id] !== undefined;
                return (
                  <tr key={c.invoice_id}
                       className={`border-t hover:bg-slate-50 ${selected ? "bg-blue-50" : ""}`}
                       data-testid={`alloc-candidate-${c.invoice_id}`}>
                    <td className="p-2">
                      <input id={`alloc-check-${c.invoice_id}`} name={`alloc-check-${c.invoice_id}`}
                             type="checkbox" checked={selected}
                             onChange={() => autoFill(c.invoice_id, c.remaining_ttc)}
                             data-testid={`alloc-check-${c.invoice_id}`} />
                    </td>
                    <td className="p-2 font-mono text-xs">{c.invoice_number || "—"}</td>
                    <td className="p-2 max-w-[180px] truncate" title={c.party_name}>{c.party_name || "—"}</td>
                    <td className="p-2 text-xs">{c.due_date ? fmtDate(c.due_date) : "—"}</td>
                    <td className="p-2 text-right font-mono text-slate-500">{eur(c.total_ttc)}</td>
                    <td className="p-2 text-right font-mono font-semibold">{eur(c.remaining_ttc)}</td>
                    <td className="p-2 text-right">
                      <Input type="number" step="0.01" min="0" max={c.remaining_ttc}
                             value={allocations[c.invoice_id] ?? ""}
                             disabled={!selected}
                             onChange={(e) => setAmount(c.invoice_id, e.target.value)}
                             className="h-7 text-right font-mono text-xs"
                             data-testid={`alloc-amount-${c.invoice_id}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onBack}>← Retour</Button>
        <Button onClick={submit} disabled={!canSubmit || submitting}
                data-testid="submit-allocations-btn"
                className="bg-blue-600 hover:bg-blue-700">
          {submitting
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Rapprochement…</>
            : <><Link2 className="w-4 h-4 mr-2" />
                Allouer {eur(totalAllocated)} sur {selectedIds.length} facture(s)
              </>}
        </Button>
      </DialogFooter>
    </div>
  );
}

// =========================================================================
// ANOMALIES
// =========================================================================
function Anomalies() {
  const [tab, setTab] = useState("unmatched");
  const [data, setData] = useState([]);
  const load = () => api.get(`/reconciliation/anomalies/${tab}`)
    .then((r) => setData(r.data.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [tab]);
  return (
    <div className="space-y-3">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="unmatched" data-testid="anom-unmatched">Sans correspondance (>7j)</TabsTrigger>
          <TabsTrigger value="pending" data-testid="anom-pending">À valider</TabsTrigger>
          <TabsTrigger value="sepa-rejections" data-testid="anom-rejects">Rejets SEPA</TabsTrigger>
          <TabsTrigger value="duplicates" data-testid="anom-duplicates">Doublons</TabsTrigger>
        </TabsList>
      </Tabs>
      <Card><CardContent className="p-0">
        {data.length === 0
          ? <div className="p-8 text-center text-slate-500 text-sm"><CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />Aucune anomalie 👍</div>
          : (
            <table className="w-full text-sm">
              <tbody>
                {data.map((r, i) => (
                  <tr key={r.id || i} className="border-b">
                    <td className="p-2">{fmtDate(r.date)}</td>
                    <td className="p-2 max-w-md truncate">{r.description || JSON.stringify(r._id || {}).slice(0, 80)}</td>
                    <td className="p-2 text-right font-mono">{r.amount != null ? eur(r.amount) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </CardContent></Card>
    </div>
  );
}

// =========================================================================
// REMISES SEPA
// =========================================================================
function RemisesSepa() {
  const [remises, setRemises] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [manualLine, setManualLine] = useState(null); // {rid, line, candidates}
  const [previewCandidate, setPreviewCandidate] = useState(null);
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null); // {html, subject, orphan_count, ...}

  const openSettings = async () => {
    try {
      const r = await api.get("/reconciliation/remises-sepa/notifications/settings");
      setSettings(r.data);
      setSettingsOpen(true);
      setEmailPreview(null);
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveSettings = async () => {
    try {
      await api.put("/reconciliation/remises-sepa/notifications/settings", settings);
      toast.success("Paramètres enregistrés");
      setSettingsOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };
  const testMonthlyReminder = async () => {
    try {
      const r = await api.post("/reconciliation/remises-sepa/notifications/monthly-reminder-run");
      if (r.data.email_sent) {
        toast.success(`✓ Email envoyé (${r.data.orphan_count} remise(s) à importer)`);
      } else {
        toast.info(`Aucun envoi — ${r.data.reason || "0 remise orpheline"}`);
      }
    } catch (e) { toast.error(apiError(e)); }
  };
  const previewMonthlyReminder = async () => {
    try {
      const days = settings?.monthly_reminder_min_days_since_tx || 3;
      const r = await api.get(
        `/reconciliation/remises-sepa/notifications/monthly-reminder-preview?min_days=${days}`);
      setEmailPreview(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = () => api.get("/reconciliation/remises-sepa")
    .then((r) => setRemises(r.data.remises))
    .catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0, ko = 0, totalMatched = 0, totalBenef = 0, totalLinked = 0;
    try {
      // Upload séquentiel — Gemini a un rate-limit par tenant ; en parallèle
      // on risque de dépasser la QoS. La séquence est ~5-10 sec par PDF.
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const r = await fetch(`${API}/reconciliation/remises-sepa/upload-pdf`, {
            method: "POST", credentials: "include", body: fd,
          });
          if (!r.ok) {
            const txt = await r.text();
            toast.error(`${file.name} : ${txt.slice(0, 160)}`);
            ko++;
            continue;
          }
          const data = await r.json();
          ok++;
          totalMatched += (data.matched_count || 0);
          totalBenef += (data.nombre_beneficiaires || 0);
          if (data.bridge_transaction_id) totalLinked++;
        } catch (err) {
          toast.error(`${file.name} : ${(err.message || "").slice(0, 160)}`);
          ko++;
        }
      }
      if (ok > 0) {
        const linkMsg = totalLinked > 0 ? ` · ${totalLinked} tx Bridge auto-liée(s)` : "";
        toast.success(
          `✓ ${ok}/${files.length} remise(s) importée(s) · `
          + `${totalMatched}/${totalBenef} factures rapprochées${linkMsg}`
        );
      }
      if (ko > 0 && ok === 0) {
        toast.error(`Aucun fichier importé (${ko} erreur(s))`);
      }
      load();
    } finally { setUploading(false); e.target.value = ""; }
  };

  const bulkAutoLink = async () => {
    setAutoLinking(true);
    try {
      const r = await api.post("/reconciliation/remises-sepa/link-transactions");
      const { linked_count, skipped_count } = r.data;
      if (linked_count > 0) {
        toast.success(`✓ ${linked_count} remise(s) auto-liée(s) à leur transaction Bridge`);
      } else {
        toast.info(`Aucune nouvelle liaison — ${skipped_count} remise(s) sans tx Bridge correspondante`);
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAutoLinking(false); }
  };

  const toggleExpand = async (rid) => {
    if (expanded === rid) { setExpanded(null); setDetail(null); return; }
    setExpanded(rid);
    try {
      const r = await api.get(`/reconciliation/remises-sepa/${rid}`);
      setDetail(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openManualMatch = async (rid, line) => {
    try {
      const r = await api.get(`/reconciliation/remises-sepa/${rid}/lines/${line.id}/candidates`);
      setManualLine({ rid, line, candidates: r.data.candidates });
      // Pré-sélectionne le 1er candidat avec un PDF pour un preview immédiat
      const firstWithPdf = (r.data.candidates || []).find((c) => c.file_asset_id);
      setPreviewCandidate(firstWithPdf || (r.data.candidates || [])[0] || null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmManualMatch = async (invoiceId) => {
    if (!manualLine) return;
    try {
      const r = await api.post(
        `/reconciliation/remises-sepa/${manualLine.rid}/lines/${manualLine.line.id}/match`,
        { invoice_id: invoiceId });
      toast.success(`✓ Rapprochée avec ${r.data.invoice_number} (${r.data.matched_count}/${r.data.total})`);
      setManualLine(null);
      setPreviewCandidate(null);
      // Recharge le détail
      const d = await api.get(`/reconciliation/remises-sepa/${manualLine.rid}`);
      setDetail(d.data);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const unmatchLine = async (rid, lineId) => {
    if (!window.confirm("Défaire ce rapprochement ? La facture repassera en 'émise'.")) return;
    try {
      await api.post(`/reconciliation/remises-sepa/${rid}/lines/${lineId}/unmatch`);
      toast.success("Ligne dérapprochée");
      const d = await api.get(`/reconciliation/remises-sepa/${rid}`);
      setDetail(d.data);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rematch = async (rid) => {
    try {
      const r = await api.post(`/reconciliation/remises-sepa/${rid}/rematch`);
      toast.success(`+${r.data.newly_matched} rapprochée(s)`);
      setDetail(null); setExpanded(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (rid) => {
    if (!window.confirm("Supprimer cette remise ? Les factures matchées repasseront à 'issued'.")) return;
    try {
      const r = await api.delete(`/reconciliation/remises-sepa/${rid}`);
      toast.success(`Remise supprimée (${r.data.rolled_back_invoices} factures rétablies)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-purple-600" />
            Remises SEPA (prélèvements clients)
          </CardTitle>
          <p className="text-sm text-slate-500">
            Uploadez un PDF de remise SDD : Gemini extrait chaque bénéficiaire, Averdy Agency
            rapproche automatiquement avec vos factures ventes (client + montant ±0.02€,
            fenêtre M/M-1) et marque les factures « payée ».
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-block">
              <input id="upload-remise-input" name="upload-remise-input"
                     type="file" accept="application/pdf" multiple className="hidden"
                     onChange={onUpload} disabled={uploading}
                     data-testid="upload-remise-input" />
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm text-white cursor-pointer
                ${uploading ? "bg-slate-400" : "bg-purple-600 hover:bg-purple-700"}`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Extraction + matching…" : "Uploader une ou plusieurs remises SEPA (PDF)"}
              </span>
            </label>
            <Button variant="outline" size="sm"
                    onClick={bulkAutoLink} disabled={autoLinking}
                    data-testid="btn-bulk-auto-link"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50">
              {autoLinking
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Link2 className="w-4 h-4 mr-1" />}
              Auto-lier aux transactions Bridge
            </Button>
            <Button variant="outline" size="sm"
                    onClick={openSettings}
                    data-testid="btn-open-notif-settings"
                    className="border-slate-300 text-slate-700 hover:bg-slate-50">
              <Sparkles className="w-4 h-4 mr-1" />
              Notifications email
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modal paramètres notifications */}
      <Dialog open={!!settingsOpen} onOpenChange={(o) => !o && setSettingsOpen(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Notifications email — Remises SEPA</DialogTitle>
            <DialogDescription>
              Configurez les destinataires des emails Averdy Agency liés aux remises SDD.
              Aucun email n'est envoyé si la case est décochée ou si la liste est vide.
            </DialogDescription>
          </DialogHeader>
          {settings && (
            <div className="space-y-6 py-2">
              <section className="rounded border p-3 bg-slate-50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox"
                         checked={settings.import_recap_enabled}
                         onChange={(e) => setSettings({
                           ...settings, import_recap_enabled: e.target.checked
                         })}
                         data-testid="settings-import-recap-enabled" />
                  <span className="text-sm font-medium">Email récap à chaque import de remise</span>
                </label>
                <p className="text-xs text-slate-500 mt-1 ml-6">
                  À la fin d'un upload PDF, envoie un email récapitulatif : matchées / orphelines / total.
                </p>
                <div className="mt-2 ml-6">
                  <label className="block text-xs text-slate-600 mb-1">
                    Destinataires (emails séparés par virgule)
                  </label>
                  <input type="text"
                         value={(settings.import_recap_recipients || []).join(", ")}
                         onChange={(e) => setSettings({
                           ...settings,
                           import_recap_recipients: e.target.value
                             .split(",").map(s => s.trim()).filter(Boolean),
                         })}
                         data-testid="settings-import-recap-recipients"
                         placeholder="cfo@monentreprise.fr, compta@monentreprise.fr"
                         className="w-full px-2 py-1 text-sm border rounded" />
                </div>
              </section>

              <section className="rounded border p-3 bg-slate-50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox"
                         checked={settings.monthly_reminder_enabled}
                         onChange={(e) => setSettings({
                           ...settings, monthly_reminder_enabled: e.target.checked
                         })}
                         data-testid="settings-monthly-reminder-enabled" />
                  <span className="text-sm font-medium">Rappel mensuel des remises non importées</span>
                </label>
                <p className="text-xs text-slate-500 mt-1 ml-6">
                  Le 1er du mois, si Averdy Agency détecte des transactions "Rem Sdd" sur le
                  relevé Bridge sans PDF correspondant importé, un email liste les
                  remises manquantes.
                </p>
                <div className="mt-2 ml-6 grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Destinataires</label>
                    <input type="text"
                           value={(settings.monthly_reminder_recipients || []).join(", ")}
                           onChange={(e) => setSettings({
                             ...settings,
                             monthly_reminder_recipients: e.target.value
                               .split(",").map(s => s.trim()).filter(Boolean),
                           })}
                           data-testid="settings-monthly-reminder-recipients"
                           placeholder="cfo@monentreprise.fr"
                           className="w-full px-2 py-1 text-sm border rounded" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">
                      Délai min (jours depuis la tx)
                    </label>
                    <input type="number" min="0" max="90"
                           value={settings.monthly_reminder_min_days_since_tx || 3}
                           onChange={(e) => setSettings({
                             ...settings,
                             monthly_reminder_min_days_since_tx: parseInt(e.target.value) || 3,
                           })}
                           data-testid="settings-monthly-reminder-delay"
                           className="w-full px-2 py-1 text-sm border rounded" />
                  </div>
                </div>
                <div className="mt-2 ml-6 flex flex-wrap gap-2">
                  <Button variant="ghost" size="sm"
                          onClick={previewMonthlyReminder}
                          data-testid="btn-preview-monthly-reminder"
                          className="text-xs h-7 text-purple-600 hover:bg-purple-50">
                    <FileText className="w-3 h-3 mr-1" />
                    Voir l'aperçu email
                  </Button>
                  <Button variant="ghost" size="sm"
                          onClick={testMonthlyReminder}
                          data-testid="btn-test-monthly-reminder"
                          className="text-xs h-7 text-blue-600 hover:bg-blue-50">
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Tester maintenant (envoi immédiat)
                  </Button>
                </div>
              </section>

              {/* Preview live du HTML email */}
              {emailPreview && (
                <section className="rounded border-2 border-purple-200 bg-white">
                  <div className="px-3 py-2 border-b bg-purple-50 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-slate-500">Sujet :</div>
                      <div className="text-sm font-medium">{emailPreview.subject}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={emailPreview.would_send
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-800"}>
                        {emailPreview.would_send
                          ? "✓ Sera envoyé"
                          : "⚠ Ne sera pas envoyé"}
                      </Badge>
                      <button onClick={() => setEmailPreview(null)}
                              className="text-xs text-slate-500 hover:text-slate-900">
                        Fermer
                      </button>
                    </div>
                  </div>
                  <div className="px-3 py-1.5 border-b bg-slate-50 text-[11px] text-slate-600 flex flex-wrap gap-4">
                    <span>Destinataires : <b>{emailPreview.recipients.length
                      ? emailPreview.recipients.join(", ")
                      : "aucun configuré"}</b>
                      {emailPreview.recipients_source === "fallback_cfo_email" && (
                        <span className="text-amber-600 ml-1">(fallback tenant_settings.cfo_email)</span>
                      )}
                    </span>
                    <span>Remises orphelines : <b>{emailPreview.orphan_count}</b></span>
                    <span>Total à rapprocher : <b>{eur(emailPreview.total_amount)}</b></span>
                  </div>
                  <iframe
                    srcDoc={emailPreview.html}
                    title="Aperçu email"
                    data-testid="email-preview-iframe"
                    sandbox=""
                    className="w-full h-72 rounded-b" />
                </section>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}
                    data-testid="btn-cancel-settings">
              Annuler
            </Button>
            <Button onClick={saveSettings} disabled={!settings}
                    data-testid="btn-save-settings"
                    className="bg-purple-600 hover:bg-purple-700">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader><CardTitle className="text-base">Historique des remises</CardTitle></CardHeader>
        <CardContent className="p-0">
          {remises.length === 0
            ? <p className="p-4 text-slate-500 text-sm">Aucune remise SEPA importée.</p>
            : <div className="divide-y">
                {remises.map((r) => (
                  <div key={r.id} data-testid={`remise-${r.id}`}>
                    <div className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer"
                         onClick={() => toggleExpand(r.id)}>
                      <div className="flex items-center gap-3 flex-1">
                        {expanded === r.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        <div>
                          <p className="text-sm font-medium">{r.reference || r.filename}</p>
                          <p className="text-xs text-slate-500">
                            Exécution : {fmtDate(r.date_exec)} · {r.nombre_beneficiaires} bénéficiaire(s)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-mono">{eur(r.total_montant)}</span>
                        <Badge className={r.matched_count === r.nombre_beneficiaires
                          ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}>
                          {r.matched_count}/{r.nombre_beneficiaires} rapprochées
                        </Badge>
                        {r.bridge_transaction_id
                          ? <Badge className="bg-blue-100 text-blue-700">
                              <Link2 className="w-3 h-3 mr-1" />Liée Bridge
                            </Badge>
                          : <Badge variant="outline" className="text-xs">Sans REM SDD</Badge>}
                        {r.source_pdf_asset_id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`${API}/files/${r.source_pdf_asset_id}`,
                                          "_blank", "noopener");
                            }}
                            title="Voir le PDF source de la remise"
                            data-testid={`view-remise-pdf-${r.id}`}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-1 transition-colors">
                            <FileText className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {expanded === r.id && detail && detail.id === r.id && (
                      <div className="bg-slate-50 p-4 space-y-3">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => rematch(r.id)}
                                   data-testid={`rematch-${r.id}`}>
                            <RefreshCw className="w-3 h-3 mr-1" />Relancer rapprochement
                          </Button>
                          <Button size="sm" variant="outline"
                                   className="text-red-600 hover:bg-red-50"
                                   onClick={() => remove(r.id)}
                                   data-testid={`delete-remise-${r.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" />Supprimer
                          </Button>
                        </div>
                        <table className="w-full text-xs bg-white rounded border">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="p-2 text-left">Bénéficiaire</th>
                              <th className="p-2 text-right">Montant</th>
                              <th className="p-2 text-left">Facture matchée</th>
                              <th className="p-2 text-left">Statut</th>
                              <th className="p-2 text-right w-40">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.lines || []).map((l) => (
                              <tr key={l.id} className="border-t">
                                <td className="p-2">{l.nom}</td>
                                <td className="p-2 text-right font-mono">{eur(l.montant)}</td>
                                <td className="p-2">
                                  {l.matched_invoice_number
                                    ? <span className="text-emerald-700 inline-flex items-center gap-1">
                                        <span>{l.matched_invoice_number} — {l.matched_client_name}</span>
                                        {l.matched_manually && (
                                          <span className="text-[9px] text-slate-500">(manuel)</span>
                                        )}
                                        {l.matched_file_asset_id && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(`${API}/files/${l.matched_file_asset_id}`, "_blank", "noopener");
                                            }}
                                            title="Ouvrir la facture (PDF)"
                                            data-testid={`view-invoice-pdf-${l.id}`}
                                            className="ml-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-0.5 transition-colors">
                                            <FileText className="w-3 h-3" />
                                          </button>
                                        )}
                                      </span>
                                    : <span className="text-slate-400">—</span>}
                                </td>
                                <td className="p-2">
                                  {l.status === "matched"
                                    ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Rapprochée</Badge>
                                    : <Badge className="bg-amber-100 text-amber-800"><AlertCircle className="w-3 h-3 mr-1" />À traiter</Badge>}
                                </td>
                                <td className="p-2 text-right">
                                  {l.status === "matched"
                                    ? <Button variant="ghost" size="sm"
                                              onClick={() => unmatchLine(r.id, l.id)}
                                              data-testid={`unmatch-line-${l.id}`}
                                              className="h-7 text-[11px] text-slate-500 hover:text-rose-600">
                                        <Trash2 className="w-3 h-3 mr-1" />Défaire
                                      </Button>
                                    : <Button variant="outline" size="sm"
                                              onClick={() => openManualMatch(r.id, l)}
                                              data-testid={`match-line-${l.id}`}
                                              className="h-7 text-[11px] border-purple-200 text-purple-700 hover:bg-purple-50">
                                        <LinkIcon className="w-3 h-3 mr-1" />Rapprocher
                                      </Button>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>}
        </CardContent>
      </Card>

      {/* Dialog rapprochement manuel d'une ligne unmatched */}
      <Dialog open={!!manualLine} onOpenChange={(o) => { if (!o) { setManualLine(null); setPdfFullscreen(false); } }}>
        <DialogContent className={cn(pdfFullscreen ? "max-w-[95vw]" : "max-w-5xl")}>
          <DialogHeader>
            <DialogTitle>
              Rapprocher manuellement
            </DialogTitle>
            <DialogDescription>
              {manualLine && (
                <>
                  Ligne : <b>{manualLine.line.nom}</b> · Montant :{" "}
                  <span className="font-mono">{eur(manualLine.line.montant)}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-5 gap-3 max-h-[75vh]">
            {/* Colonne gauche — liste candidats (masquée en plein écran) */}
            {!pdfFullscreen && (
              <div className="col-span-2 overflow-y-auto space-y-1 pr-1 max-h-[70vh]">
                {(manualLine?.candidates || []).length === 0
                  ? <p className="text-sm text-slate-500 p-4">Aucune facture non payée trouvée.</p>
                  : (manualLine.candidates.map((c) => (
                    <div key={c.id}
                         className={cn(
                           "rounded border transition-colors",
                           previewCandidate?.id === c.id ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-purple-400 hover:bg-purple-50/40",
                           c.amount_match && previewCandidate?.id !== c.id ? "bg-emerald-50/40 border-emerald-200" : ""
                         )}>
                      <button
                        onClick={() => setPreviewCandidate(c)}
                        data-testid={`candidate-invoice-${c.id}`}
                        className="w-full text-left p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {c.invoice_number || "(sans n°)"} — {c.client_name || "—"}
                            </div>
                            <div className="text-xs text-slate-500">
                              Émise : {fmtDate(c.issued_at)}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-mono text-sm">{eur(c.total_ttc)}</div>
                            <div className={cn(
                              "text-[10px]",
                              c.amount_match ? "text-emerald-700" : "text-amber-700"
                            )}>
                              {c.amount_match
                                ? "✓ Même montant"
                                : `Écart ${c.delta_amount > 0 ? "+" : ""}${c.delta_amount.toFixed(2)} €`}
                            </div>
                          </div>
                        </div>
                      </button>
                      <div className="flex items-center gap-1 px-3 pb-2">
                        {c.file_asset_id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewCandidate(c);
                            }}
                            data-testid={`preview-pdf-${c.id}`}
                            className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-100">
                            <FileText className="w-3 h-3" /> Aperçu
                          </button>
                        )}
                        {c.file_asset_id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`${API}/files/${c.file_asset_id}`, "_blank", "noopener");
                            }}
                            data-testid={`open-pdf-${c.id}`}
                            className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-100">
                            <ArrowUpRight className="w-3 h-3" /> Ouvrir
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); confirmManualMatch(c.id); }}
                          data-testid={`confirm-match-${c.id}`}
                          className="ml-auto text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white font-medium">
                          <CheckCircle2 className="w-3 h-3" /> Rapprocher
                        </button>
                      </div>
                    </div>
                  )))}
              </div>
            )}
            {/* Colonne droite — preview PDF (iframe) */}
            <div className={cn(
              "border rounded bg-slate-50 flex flex-col min-h-[60vh]",
              pdfFullscreen ? "col-span-5" : "col-span-3"
            )}>
              {previewCandidate?.file_asset_id ? (
                <>
                  <div className="px-3 py-2 border-b bg-white text-xs text-slate-600 flex items-center justify-between gap-2">
                    <span className="font-medium truncate flex-1">
                      {previewCandidate.invoice_number} — {previewCandidate.client_name}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setPdfFullscreen((v) => !v)}
                        title={pdfFullscreen ? "Réduire (afficher la liste)" : "Agrandir (masquer la liste)"}
                        data-testid="toggle-pdf-fullscreen"
                        className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded p-1 inline-flex items-center gap-1">
                        {pdfFullscreen
                          ? <><Minimize2 className="w-3.5 h-3.5" /> Réduire</>
                          : <><Maximize2 className="w-3.5 h-3.5" /> Agrandir</>}
                      </button>
                      <button
                        onClick={() => window.open(`${API}/files/${previewCandidate.file_asset_id}`, "_blank", "noopener")}
                        title="Ouvrir dans un nouvel onglet"
                        className="text-blue-600 hover:underline inline-flex items-center gap-1">
                        <ArrowUpRight className="w-3 h-3" /> Nouvel onglet
                      </button>
                      {pdfFullscreen && (
                        <button
                          onClick={(e) => { e.stopPropagation(); confirmManualMatch(previewCandidate.id); }}
                          data-testid="fullscreen-confirm-match"
                          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white font-medium">
                          <CheckCircle2 className="w-3 h-3" /> Rapprocher cette facture
                        </button>
                      )}
                    </div>
                  </div>
                  <iframe
                    key={previewCandidate.id}
                    src={`${API}/files/${previewCandidate.file_asset_id}#toolbar=0&navpanes=0`}
                    title="Aperçu facture"
                    data-testid="candidate-pdf-preview"
                    className={cn("w-full rounded-b", pdfFullscreen ? "flex-1 min-h-[80vh]" : "flex-1")} />
                </>
              ) : previewCandidate ? (
                <div className="flex-1 flex items-center justify-center text-slate-500 text-sm p-6 text-center">
                  Cette facture n'a pas de PDF attaché.<br />
                  <span className="text-xs">(facture manuelle sans document scanné)</span>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-6 text-center">
                  <div>
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    Sélectionnez une facture à gauche pour voir son PDF ici.
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setManualLine(null); setPreviewCandidate(null); setPdfFullscreen(false); }}
                    data-testid="close-manual-match">
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================================
// RELEVÉS BANCAIRES — Storage + visualisation + push dossier compta
// =========================================================================
function Statements() {
  const [statements, setStatements] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/reconciliation/statements")
    .then((r) => setStatements(r.data.statements))
    .catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0, ko = 0;
    try {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const r = await fetch(`${API}/reconciliation/statements/upload-pdf`, {
            method: "POST", credentials: "include", body: fd,
          });
          if (!r.ok) { toast.error(`${file.name} : ${(await r.text()).slice(0,160)}`); ko++; continue; }
          await r.json();
          ok++;
        } catch (err) {
          toast.error(`${file.name} : ${(err.message||"").slice(0,160)}`); ko++;
        }
      }
      if (ok > 0) toast.success(`✓ ${ok}/${files.length} relevé(s) archivé(s)`);
      if (ko > 0 && ok === 0) toast.error(`Aucun fichier importé (${ko} erreur(s))`);
      load();
    } finally { setUploading(false); e.target.value = ""; }
  };

  const sendToFolder = async (sid) => {
    try {
      const r = await api.post(`/reconciliation/statements/${sid}/send-to-folder`, {});
      if (r.data.already_attached) {
        toast.info(`Déjà dans le dossier ${r.data.folder_month || "compta"}`);
      } else {
        toast.success(`✓ Envoyé au dossier compta ${r.data.folder_month || ""}`);
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (sid) => {
    if (!window.confirm("Supprimer ce relevé ?")) return;
    try {
      await api.delete(`/reconciliation/statements/${sid}`);
      toast.success("Relevé supprimé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            Relevés bancaires — archive mensuelle
          </CardTitle>
          <p className="text-sm text-slate-500">
            Uploadez chaque mois vos relevés PDF pour les archiver, les visualiser
            à tout moment et les pousser en un clic vers le dossier comptable du mois.
            L'IA extrait uniquement la période et l'IBAN pour éviter les doublons —
            aucune transaction n'est insérée en base. Plusieurs banques sur le même
            mois sont autorisées ; un même IBAN + mois est bloqué.
          </p>
        </CardHeader>
        <CardContent>
          <label className="inline-block">
            <input id="upload-pdf-input" name="upload-pdf-input"
                   type="file" accept="application/pdf" multiple className="hidden"
                   onChange={onUpload} disabled={uploading}
                   data-testid="upload-pdf-input" />
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm text-white cursor-pointer
              ${uploading ? "bg-slate-400" : "bg-blue-600 hover:bg-blue-700"}`}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Archivage…" : "Uploader un ou plusieurs relevés (PDF)"}
            </span>
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Historique des relevés</CardTitle></CardHeader>
        <CardContent className="p-0">
          {statements.length === 0
            ? <p className="p-4 text-slate-500 text-sm">Aucun relevé archivé.</p>
            : <table className="w-full text-sm">
                <thead className="border-b bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-2">Fichier</th>
                    <th className="text-left p-2">Mois</th>
                    <th className="text-left p-2">Banque</th>
                    <th className="text-left p-2">IBAN</th>
                    <th className="text-left p-2">Dossier compta</th>
                    <th className="text-right p-2 w-40">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((s) => (
                    <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`statement-${s.id}`}>
                      <td className="p-2">
                        <FileText className="inline w-3 h-3 mr-1" />{s.filename}
                      </td>
                      <td className="p-2 font-mono text-xs">{s.month || "—"}</td>
                      <td className="p-2 text-xs">{s.bank_name || <span className="text-slate-400">—</span>}</td>
                      <td className="p-2 font-mono text-[11px]">{s.iban ? `…${s.iban.slice(-6)}` : <span className="text-slate-400">—</span>}</td>
                      <td className="p-2 text-xs">
                        {s.sent_to_folder_id
                          ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                              <CheckCircle2 className="w-3 h-3 mr-1" />Envoyé
                            </Badge>
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {s.source_pdf_asset_id && (
                            <button
                              onClick={() => window.open(`${API}/files/${s.source_pdf_asset_id}`, "_blank", "noopener")}
                              title="Voir le PDF"
                              data-testid={`view-statement-${s.id}`}
                              className="text-blue-600 hover:bg-blue-50 rounded p-1">
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {s.source_pdf_asset_id && !s.sent_to_folder_id && (
                            <button
                              onClick={() => sendToFolder(s.id)}
                              title="Envoyer au dossier comptable du mois"
                              data-testid={`send-to-folder-${s.id}`}
                              className="text-emerald-600 hover:bg-emerald-50 rounded p-1">
                              <ArrowUpRight className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => remove(s.id)}
                            title="Supprimer"
                            data-testid={`delete-statement-${s.id}`}
                            className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded p-1">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>}
        </CardContent>
      </Card>
    </div>
  );
}
